/**
 * Arrow library — DERIVED from the canonical catalog. Do not add entries here.
 *
 * 2026-06-10: this file used to carry its own hand-curated arrow list
 * (~32 models) which silently drifted from the canonical catalog in
 * `src/data/shafts.ts` (135+ models) — testers saw "missing" Victory and
 * Easton arrows in the My Arrows picker while the main Forge builder had
 * them. ARROW_LIBRARY is now generated from SHAFTS at module load, so the
 * pickers always show exactly the canonical catalog. To add an arrow, add
 * it to the MAIN repo's `tools/arrow-builder-react/src/data/shafts.ts` and
 * run `npm run lib:sync` there (see scripts/check-library-sync.sh gate).
 *
 * Rich marketing metadata that the canonical schema doesn't model
 * (insert systems, vane attachment, construction) lives in the small
 * OVERLAY table below, keyed by brand|model.
 */

import { SHAFTS } from '@/data/shafts';
import type { Shaft } from '@/types';

export interface ArrowSpec {
  spine: number;            // numeric spine value
  gpi: number;              // grains per inch
  insideDiameterIn: number; // ID
  outsideDiameterIn: number;// OD
}

export type VaneAttachmentStyle =
  | 'standard-square'
  | 'pre-fletched'           // shipped fletched
  | 'helical-only'
  | 'wrap-required';

export interface Arrow {
  brand: string;
  model: string;
  /** Marketing category */
  category: 'hunting' | 'target-3d' | 'crossover' | 'long-range-hunting' | 'youth';
  /** Spine options available */
  spines: ReadonlyArray<ArrowSpec>;
  /** Vane attachment style */
  vaneAttachment: VaneAttachmentStyle;
  /** Whether a proprietary insert/outsert system ships with the arrow */
  shipsWithInsertSystem: boolean;
  /** Insert/outsert system name + weight (gr), if shipped */
  insertSystem?: {
    name: string;
    grainWeight: number;
    description: string;
  };
  /** Coating / surface treatment if marketing-relevant */
  surfaceTreatment?: string;
  /** Carbon weave description */
  carbonConstruction?: string;
  /** Notes */
  notes?: string;
  verificationStatus: 'verified' | 'pending';
  manufacturerUrl?: string;
}

/** Marketing metadata the canonical Shaft schema doesn't carry. */
const OVERLAY: Record<string, Partial<Arrow>> = {
  'UltraView|UV 1K': {
    vaneAttachment: 'pre-fletched',
    shipsWithInsertSystem: true,
    insertSystem: {
      name: 'UV Outsert',
      grainWeight: 75,
      description: 'Proprietary UltraView outsert system. Stainless steel core + aluminum housing. Protects the shaft end and optimizes FOC. 75gr.',
    },
    surfaceTreatment: 'Satin Ice nano-ceramic coating — improves penetration and target removal',
    carbonConstruction: '1K carbon weave (1,000 fibers per tow)',
    manufacturerUrl: 'https://ultraviewarchery.com/products/uv-1k-shafts',
  },
  'Victory|VAP TKO': { manufacturerUrl: 'https://www.victoryarchery.com/' },
  'Easton|5mm Axis Carbon': { manufacturerUrl: 'https://eastonarchery.com/' },
};

/** Map canonical `use` strings → marketing category. */
function toCategory(use: string): Arrow['category'] {
  const u = use.toLowerCase();
  if (u.includes('hunting') && u.includes('target')) return 'crossover';
  if (u.includes('target')) return 'target-3d';
  if (u.includes('crossbow')) return 'crossover';
  if (u.includes('long range') || u.includes('efoc') || u.includes('micro')) return 'long-range-hunting';
  return 'hunting';
}

/** Parse a display OD like '.245"' or '.229–.248"' → numeric inches (midpoint for ranges). */
function parseOd(od: string): number {
  const nums = od.match(/\.\d+/g)?.map(Number) ?? [];
  if (nums.length === 0) return 0;
  return nums.length === 1 ? nums[0] : (nums[0] + nums[nums.length - 1]) / 2;
}

function toArrow(s: Shaft): Arrow {
  const idIn = s.shaftId ? Number(s.shaftId) : 0;
  const fallbackOd = parseOd(s.od);
  const spines: ArrowSpec[] = Object.entries(s.spines)
    .map(([spine, gpi]) => ({
      spine: Number(spine),
      gpi,
      insideDiameterIn: idIn,
      outsideDiameterIn: s.perSpineOd?.[Number(spine)] ?? fallbackOd,
    }))
    .sort((a, b) => a.spine - b.spine);
  const overlay = OVERLAY[`${s.brand}|${s.model}`] ?? {};
  return {
    brand: s.brand,
    model: s.model,
    category: toCategory(s.use),
    spines,
    vaneAttachment: 'standard-square',
    shipsWithInsertSystem: false,
    notes: s.notes,
    verificationStatus: /verified/i.test(s.notes) ? 'verified' : 'pending',
    ...overlay,
  };
}

export const ARROW_LIBRARY: ReadonlyArray<Arrow> = SHAFTS
  .filter((s) => !s.isCustom)
  .map(toArrow);

/**
 * Look up an arrow by exact brand + model.
 */
export function getArrow(brand: string, model: string): Arrow | undefined {
  return ARROW_LIBRARY.find((a) => a.brand === brand && a.model === model);
}

/**
 * Filter arrows by brand.
 */
export function arrowsByBrand(brand: string): Arrow[] {
  return ARROW_LIBRARY.filter((a) => a.brand === brand);
}

/**
 * Find arrows by spine — useful for the spine calculator's matching pass.
 */
export function arrowsBySpine(spine: number, tolerance = 25): Arrow[] {
  return ARROW_LIBRARY.filter((a) =>
    a.spines.some((s) => Math.abs(s.spine - spine) <= tolerance)
  );
}
